import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ASSET_TYPE,
  AssetItem,
  AssetReport,
  REPORT_ITEM_TYPE,
  REPORT_OWNER_TYPE,
  ReportOwnerType,
} from '../../type/AssetReport.type';
import AssetReportModalEditTable from './AssetReportModalEditTable';
import { useAssetReportAPI } from '../../api/AssetReport.api';

export interface EditAssetReportModalData {
  isOpen: boolean;
  assetReport: AssetReport;
  onClose: () => void;
}

const EditAssetReportModal = ({ isOpen, assetReport, onClose }: EditAssetReportModalData) => {
  const { updateAssetReport } = useAssetReportAPI();
  const [editAssetReport, setEditAssetReport] = useState<AssetReport>(assetReport);
  const [activeDataTab, setActiveDataTab] = useState<ReportOwnerType>(REPORT_OWNER_TYPE.HUSBAND);

  useEffect(() => {
    setEditAssetReport(assetReport);
  }, [isOpen]);

  const duplicateNameList = useMemo(
    () =>
      Array.from(
        new Set(
          editAssetReport.reportItemList
            .map((reportItem) => reportItem.name + '!!!!' + reportItem.type + '!!!!' + reportItem.owner)
            .filter((item, idx, self) => self.indexOf(item) !== idx),
        ),
      ),
    [editAssetReport.reportItemList],
  );

  const handleEditAssetReportChange = (target: string, value: any) => {
    setEditAssetReport({
      ...editAssetReport,
      [target]: value,
    });
  };

  const handleCloseClick = () => {
    onClose();
  };

  const handleReportItemChange = (index: number, field: any, value: string | number) => {
    handleEditAssetReportChange(
      'reportItemList',
      editAssetReport.reportItemList.map((reportItem, itemIndex) =>
        itemIndex === index ? { ...reportItem, [field]: value } : reportItem,
      ),
    );
  };

  const handleAddReportItem = () => {
    const newAssetItem: AssetItem = {
      id: uuidv4(),
      assetReportId: editAssetReport.id,
      owner: activeDataTab,
      name: '',
      comment: '',
      type: REPORT_ITEM_TYPE.ASSET,
      amount: 0,
      excludeYn: false,
      prevExistYn: false,
      assetType: ASSET_TYPE.SAVINGS,
      principalAmount: 0,
      returnRate: 0,
      dividendAmount: 0,
    };

    handleEditAssetReportChange('reportItemList', [...editAssetReport.reportItemList, newAssetItem]);
  };

  const handleDeleteReportItem = (index: number) => {
    handleEditAssetReportChange(
      'reportItemList',
      editAssetReport.reportItemList.filter((reportItem, itemIndex) => itemIndex !== index),
    );
  };

  const handleUpdateClick = async () => {
    const { data: assetReportId } = await updateAssetReport(editAssetReport.id, editAssetReport);

    if (assetReportId) {
      onClose();
    }
  };

  return isOpen ? (
    <div className="fixed min-h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">자산 수정</h2>
            <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <div className="p-6">
            <div className="mb-4 border-b border-gray-200">
              <nav className="grid grid-cols-2 w-full" aria-label="Tabs">
                <button
                  onClick={() => setActiveDataTab(REPORT_OWNER_TYPE.HUSBAND)}
                  className={`py-3 text-center text-sm font-medium ${
                    activeDataTab === REPORT_OWNER_TYPE.HUSBAND
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  남편
                </button>
                <button
                  onClick={() => setActiveDataTab(REPORT_OWNER_TYPE.WIFE)}
                  className={`py-3 text-center text-sm font-medium ${
                    activeDataTab === REPORT_OWNER_TYPE.WIFE
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  아내
                </button>
              </nav>
            </div>
            <div className="overflow-x-auto">
              <AssetReportModalEditTable
                owner={activeDataTab}
                duplicateNameList={duplicateNameList}
                reportItemList={editAssetReport.reportItemList}
                handleDeleteReportItem={handleDeleteReportItem}
                handleReportItemChange={handleReportItemChange}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleAddReportItem}
                className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 !rounded-button"
              >
                <i className="fas fa-plus mr-2"></i>
                자산 추가
              </button>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {duplicateNameList.length > 0 && (
            <div className="flex items-center text-red-600 mb-3">
              <i className="fas fa-exclamation-circle mr-2"></i>
              <span className="text-sm font-medium">
                동일 이름의 자산/부채는 존재할 수 없습니다! <br />
                {duplicateNameList.map((duplicateName) => (
                  <>
                    - {duplicateName.split('!!!!')[0]}
                    <br />
                  </>
                ))}
              </span>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCloseClick}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 !rounded-button"
            >
              취소
            </button>
            <button
              onClick={handleUpdateClick}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 !rounded-button"
              disabled={duplicateNameList.length > 0}
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default EditAssetReportModal;
